import { motion } from "framer-motion";
import { Building2, GraduationCap, PartyPopper, Heart, ArrowRight } from "lucide-react";

const events = [
  {
    icon: Building2,
    title: "House Parties",
    desc: "Turn your living room into a VR arena. Friends, fun, and unforgettable gaming nights.",
    features: ["Easy home setup", "Multiplayer games", "Weekend packages"],
  },
  {
    icon: PartyPopper,
    title: "Birthday Parties",
    desc: "Give the birthday star and guests a celebration they'll never forget with immersive VR fun.",
    features: ["Party packages", "Age-appropriate games", "Safe & supervised"],
  },
  {
    icon: GraduationCap,
    title: "Corporate Events",
    desc: "Team building, product launches, and trade shows — give your audience an unforgettable VR experience.",
    features: ["Bulk headset rentals", "On-site setup & support", "Custom branded experiences"],
  },
  {
    icon: Heart,
    title: "Casual Gatherings",
    desc: "A chill hangout with friends just got way cooler. Rent VR for your next get-together.",
    features: ["Flexible rental hours", "Easy pickup & delivery", "Great for small groups"],
  },
];

const EventsSection = () => {
  const handleEnquire = () => {
    const msg = encodeURIComponent("Hi! I'm interested in renting VR headsets for an event. Can you share more details?");
    window.open(`https://wa.me/918308832896?text=${msg}`, "_blank");
  };

  return (
    <section className="py-24 md:py-36 px-6">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6"
        >
          <span className="font-body text-sm uppercase tracking-[0.3em] text-secondary mb-4 block">
            For Events
          </span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground mb-4">
            VR Rental for Events in Pune & Mumbai
          </h2>
          <p className="font-body text-muted-foreground max-w-lg mx-auto">
            From corporate team building to college fests and birthday parties — we deliver VR headsets to your venue in Pune & Mumbai with full setup and on-site support.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mt-16">
          {events.map((event, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: i * 0.06 }}
              className="group h-full flex flex-col rounded-2xl border border-border bg-card p-7 md:p-9 transition-all duration-300 hover:border-primary/40 hover:-translate-y-0.5"
            >
              <div className="flex items-center justify-between mb-7">
                <div className="w-11 h-11 rounded-lg border border-border flex items-center justify-center transition-colors duration-300 group-hover:border-primary/50">
                  <event.icon className="w-[18px] h-[18px] text-primary" strokeWidth={1.5} />
                </div>
                <span className="font-body text-xs tracking-[0.25em] text-muted-foreground">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>

              <h3 className="font-heading text-xl font-bold text-foreground mb-3">
                {event.title}
              </h3>
              <p className="font-body text-sm text-muted-foreground leading-relaxed">
                {event.desc}
              </p>

              <ul className="mt-7 pt-6 border-t border-border space-y-3">
                {event.features.map((f, j) => (
                  <li
                    key={j}
                    className="font-body text-xs text-muted-foreground flex items-baseline gap-3"
                  >
                    <span className="w-4 h-px bg-secondary/60 flex-shrink-0 translate-y-[0.4rem]" />
                    {f}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center mt-14"
        >
          <button
            onClick={handleEnquire}
            className="inline-flex items-center gap-2 font-heading text-base font-semibold px-10 py-4 rounded-full bg-primary text-primary-foreground glow-magenta transition-all hover:opacity-90"
          >
            Enquire for Events
            <ArrowRight className="w-4 h-4" />
          </button>
          <p className="font-body text-xs text-muted-foreground mt-3">
            Custom pricing based on headset count, duration & location
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default EventsSection;
